/**
 * Manage user notifications throughout the integrations feature.
 *
 * This hook provides a centralized way to display success and error notifications
 * with automatic dismissal after a timeout. It handles cleanup of timeouts to prevent
 * memory leaks and ensures only one notification is displayed at a time.
 *
 * @returns {Object} Object containing:
 *   - notification: Current notification state (null when no notification)
 *   - showNotification: Function to show a custom notification
 *   - showError: Convenience function to show error notifications
 *   - showSuccess: Convenience function to show success notifications
 *   - clearNotification: Function to manually dismiss the current notification
 */
import { NotificationType } from "@transfr-inc/dashboard-components";
import { useEffect, useRef, useState } from "react";

const DEFAULT_TIMEOUT = 10000;

export function useNotifications() {
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Displays a notification and automatically dismisses it after the timeout.
   * If a notification is already showing, it will be replaced and the timeout reset.
   *
   * @param {Object} config - Notification configuration with type, message, and icon
   * @param {number} timeout - Time in milliseconds before auto-dismiss (default: 10000)
   */
  const showNotification = (config, timeout = DEFAULT_TIMEOUT) => {
    setNotification(config);
    // Clear any existing timeout before setting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, timeout);
  };

  const showError = (message, icon = ["fa-solid", "triangle-exclamation"]) => {
    showNotification({
      type: NotificationType.error,
      message,
      icon,
    });
  };

  const showSuccess = (message, icon = ["fa-solid", "circle-check"]) => {
    showNotification({
      type: NotificationType.success,
      message,
      icon,
    });
  };

  const clearNotification = () => {
    setNotification(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return {
    notification,
    showNotification,
    showError,
    showSuccess,
    clearNotification,
  };
}
